import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, ZoomIn, ZoomOut, Clock, Loader2, AlertTriangle, RotateCcw, Search, StickyNote, Undo2, ChevronDown, ChevronUp } from 'lucide-react';
import {
  useAdminDisputeDetail,
  useDisputeTimeline,
  useApproveDispute,
  useRejectDispute,
  useReprocessOcr,
  useForceVerify,
  useAddDisputeNote,
  useReverseDispute,
  useDetailedLog,
  DisputeStatus,
  RejectionReason,
} from '../../../hooks/useDisputes';

const STATUS_LABELS: Record<DisputeStatus, string> = {
  PENDING_OCR: 'OCR Pendente',
  PENDING_VERIFICATION: 'Verificando',
  VERIFIED: 'Verificado',
  UNVERIFIED: 'Não Verificado',
  OCR_FAILED: 'Erro OCR',
  DUPLICATE_IMAGE: 'Imagem Duplicada',
  DUPLICATE_E2E: 'E2E Duplicado',
  PENDING_REVIEW: 'Aguardando Revisão',
  PENDING_SECOND_APPROVAL: '2ª Aprovação',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  CANCELLED: 'Cancelado',
  ALREADY_CREDITED: 'Já Creditado',
};

const REJECTION_REASONS: { value: RejectionReason; label: string }[] = [
  { value: 'ILLEGIBLE', label: 'Comprovante ilegível' },
  { value: 'FAKE', label: 'Comprovante falso ou adulterado' },
  { value: 'ALREADY_CREDITED', label: 'Valor já creditado anteriormente' },
  { value: 'NOT_FOUND', label: 'Pagamento não encontrado' },
  { value: 'DATA_MISMATCH', label: 'Dados não conferem' },
  { value: 'OTHER', label: 'Outro motivo' },
];

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function DisputeReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: dispute, isLoading } = useAdminDisputeDetail(id || '');
  const { data: timeline } = useDisputeTimeline(id || '');
  const approveMutation = useApproveDispute();
  const rejectMutation = useRejectDispute();
  const reprocessOcrMutation = useReprocessOcr();
  const forceVerifyMutation = useForceVerify();
  const addNoteMutation = useAddDisputeNote();
  const reverseMutation = useReverseDispute();
  const { data: detailedLog } = useDetailedLog(id || '');

  const [zoom, setZoom] = useState(1);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [showDetailedLog, setShowDetailedLog] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [approveConfirmed, setApproveConfirmed] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejectReason, setRejectReason] = useState<RejectionReason>('NOT_FOUND');
  const [rejectReasonDetail, setRejectReasonDetail] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [reverseNotes, setReverseNotes] = useState('');
  const [reverseConfirmed, setReverseConfirmed] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!dispute) {
    return (
      <div className="max-w-lg mx-auto p-4 text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Contestação não encontrada</p>
      </div>
    );
  }

  const grossAmount = dispute.claimedAmountCents;
  const fee = Math.round(grossAmount * 0.05);
  const netAmount = grossAmount - fee;
  const isTerminal = ['APPROVED', 'REJECTED', 'CANCELLED', 'ALREADY_CREDITED'].includes(dispute.status);

  const handleApprove = async () => {
    if (approveNotes.length < 10 || !approveConfirmed) return;
    try {
      await approveMutation.mutateAsync({ id: dispute.id, notes: approveNotes, confirmed: true });
      setShowApproveModal(false);
      navigate('/admin/disputes');
    } catch { /* error handled by mutation */ }
  };

  const handleReject = async () => {
    if (rejectNotes.length < 10) return;
    try {
      await rejectMutation.mutateAsync({ id: dispute.id, notes: rejectNotes, reason: rejectReason, reasonDetail: rejectReasonDetail || undefined });
      setShowRejectModal(false);
      navigate('/admin/disputes');
    } catch { /* error handled by mutation */ }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/disputes')} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Análise de Contestação</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{dispute.user?.name} • {dispute.user?.email}</p>
          </div>
        </div>
        {/* Action Buttons (desktop) */}
        {!isTerminal && (
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => setShowRejectModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-medium"
            >
              <XCircle className="w-4 h-4" />
              Rejeitar
            </button>
            <button
              onClick={() => setShowApproveModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"
            >
              <CheckCircle className="w-4 h-4" />
              Aprovar
            </button>
          </div>
        )}
      </div>

      {/* Split Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Image */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Comprovante</h3>
            <div className="flex items-center gap-1">
              <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                <ZoomOut className="w-4 h-4 text-gray-500" />
              </button>
              <span className="text-xs text-gray-500">{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom((z) => Math.min(3, z + 0.25))} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                <ZoomIn className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
          <div className="overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 max-h-[500px]">
            <img
              src={`/api/v1/admin/disputes/${dispute.id}/image`}
              alt="Comprovante"
              className="mx-auto transition-transform"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
              onError={(e) => { (e.target as HTMLImageElement).alt = 'Imagem indisponível'; }}
            />
          </div>
        </div>

        {/* Right: Data */}
        <div className="space-y-4">
          {/* Status */}
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">Status Atual</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{STATUS_LABELS[dispute.status]}</p>
          </div>

          {/* Comparison Table: OCR vs Claimed */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Campo</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Informado</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">OCR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                <tr>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">Valor</td>
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{formatCurrency(dispute.claimedAmountCents)}</td>
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">
                    {dispute.extractedAmountCents ? formatCurrency(dispute.extractedAmountCents) : '—'}
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">Data</td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white">{new Date(dispute.claimedDate).toLocaleDateString('pt-BR')}</td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white">{dispute.extractedDate ? new Date(dispute.extractedDate).toLocaleDateString('pt-BR') : '—'}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">E2E ID</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">—</td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white text-xs font-mono">{dispute.extractedE2eId || '—'}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">Pagador</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">—</td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white text-xs font-mono">{dispute.extractedPayerDoc || '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* AtivoPay Verification */}
          {dispute.anypayVerification && (
            <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Verificação AtivoPay</p>
              <div className="flex items-center gap-2">
                {dispute.status === 'VERIFIED' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                    <CheckCircle className="w-3 h-3" /> Match encontrado
                  </span>
                ) : dispute.status === 'ALREADY_CREDITED' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                    <CheckCircle className="w-3 h-3" /> Já creditado
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                    <AlertTriangle className="w-3 h-3" /> Sem match
                  </span>
                )}
              </div>
              {dispute.anypayTransactionId && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono">
                  TX: {dispute.anypayTransactionId}
                </p>
              )}
            </div>
          )}

          {/* OCR Confidence */}
          {dispute.ocrConfidence !== undefined && (
            <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Confiança OCR</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${dispute.ocrConfidence > 70 ? 'bg-green-500' : dispute.ocrConfidence > 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${dispute.ocrConfidence}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{dispute.ocrConfidence}%</span>
              </div>
            </div>
          )}

          {/* Timeline */}
          {timeline && timeline.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Timeline</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {timeline.map((entry) => (
                  <div key={entry.id} className="flex gap-3 text-sm">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 rounded-full bg-primary-500 mt-1.5" />
                      <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700" />
                    </div>
                    <div className="pb-3">
                      <p className="text-gray-900 dark:text-white font-medium text-xs">
                        {entry.action.replace(/_/g, ' ')}
                      </p>
                      <p className="text-gray-500 dark:text-gray-400 text-xs">
                        {formatDate(entry.changedAt)}
                        {entry.notes && ` — ${entry.notes}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Action Buttons */}
      {!isTerminal && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex gap-3 md:hidden z-50">
          <button
            onClick={() => setShowRejectModal(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg font-medium"
          >
            <XCircle className="w-4 h-4" />
            Rejeitar
          </button>
          <button
            onClick={() => setShowApproveModal(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-600 text-white rounded-lg font-medium"
          >
            <CheckCircle className="w-4 h-4" />
            Aprovar
          </button>
        </div>
      )}

      {/* Ações Administrativas */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Ações Administrativas</h3>
        <div className="flex flex-wrap gap-2">
          {/* Reprocessar OCR */}
          {(dispute.status === 'OCR_FAILED' || !isTerminal) && (
            <button
              onClick={() => reprocessOcrMutation.mutate(dispute.id)}
              disabled={reprocessOcrMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 disabled:opacity-50"
            >
              {reprocessOcrMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              Reprocessar OCR
            </button>
          )}

          {/* Re-verificar AtivoPay */}
          {!isTerminal && (
            <button
              onClick={() => forceVerifyMutation.mutate(dispute.id)}
              disabled={forceVerifyMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50"
            >
              {forceVerifyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Re-verificar AtivoPay
            </button>
          )}

          {/* Adicionar Nota */}
          <button
            onClick={() => setShowNoteModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <StickyNote className="w-4 h-4" />
            Adicionar Nota
          </button>

          {/* Estornar Crédito */}
          {dispute.status === 'APPROVED' && (
            <button
              onClick={() => setShowReverseModal(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-red-500 dark:border-red-600 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 font-medium"
            >
              <Undo2 className="w-4 h-4" />
              Estornar Crédito
            </button>
          )}
        </div>

        {/* Feedback messages */}
        {reprocessOcrMutation.isSuccess && (
          <p className="text-xs text-green-600 dark:text-green-400">OCR reprocessado com sucesso.</p>
        )}
        {reprocessOcrMutation.isError && (
          <p className="text-xs text-red-600 dark:text-red-400">Erro ao reprocessar OCR.</p>
        )}
        {forceVerifyMutation.isSuccess && (
          <p className="text-xs text-green-600 dark:text-green-400">Verificação concluída.</p>
        )}
        {forceVerifyMutation.isError && (
          <p className="text-xs text-red-600 dark:text-red-400">Erro na verificação.</p>
        )}
      </div>

      {/* Log Detalhado */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <button
          onClick={() => setShowDetailedLog(!showDetailedLog)}
          className="w-full flex items-center justify-between p-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <span>Log Detalhado</span>
          {showDetailedLog ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showDetailedLog && detailedLog && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 max-h-96 overflow-y-auto">
            {/* Timing */}
            {detailedLog.processingTimes && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Tempos de Processamento</p>
                {detailedLog.processingTimes.ocrDurationMs && (
                  <p className="text-xs text-gray-600 dark:text-gray-400">OCR: {(detailedLog.processingTimes.ocrDurationMs / 1000).toFixed(1)}s</p>
                )}
                {detailedLog.processingTimes.verificationDurationMs && (
                  <p className="text-xs text-gray-600 dark:text-gray-400">Verificação: {(detailedLog.processingTimes.verificationDurationMs / 1000).toFixed(1)}s</p>
                )}
                {detailedLog.processingTimes.totalResolutionMs && (
                  <p className="text-xs text-gray-600 dark:text-gray-400">Resolução total: {(detailedLog.processingTimes.totalResolutionMs / 3600000).toFixed(1)}h</p>
                )}
              </div>
            )}

            {/* History entries */}
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Histórico Completo</p>
              {detailedLog.history?.map((entry: any) => (
                <div key={entry.id} className="text-xs p-2 rounded bg-gray-50 dark:bg-gray-800 space-y-0.5">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{entry.action.replace(/_/g, ' ')}</span>
                    <span className="text-gray-500 dark:text-gray-400">{formatDate(entry.changedAt)}</span>
                  </div>
                  {entry.changedBy && <p className="text-gray-500 dark:text-gray-400">Admin: {entry.changedBy.slice(0, 8)}...</p>}
                  {entry.ipAddress && <p className="text-gray-500 dark:text-gray-400">IP: {entry.ipAddress}</p>}
                  {entry.notes && <p className="text-gray-600 dark:text-gray-300 italic">{entry.notes}</p>}
                </div>
              ))}
            </div>

            {/* Audit logs */}
            {detailedLog.auditLogs?.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Audit Log</p>
                {detailedLog.auditLogs.map((entry: any) => (
                  <div key={entry.id} className="text-xs p-2 rounded bg-gray-50 dark:bg-gray-800 space-y-0.5">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{entry.action}</span>
                      <span className="text-gray-500 dark:text-gray-400">{formatDate(entry.createdAt)}</span>
                    </div>
                    {entry.ipAddress && <p className="text-gray-500 dark:text-gray-400">IP: {entry.ipAddress}</p>}
                    {entry.userAgent && <p className="text-gray-500 dark:text-gray-400 truncate max-w-xs">UA: {entry.userAgent}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Timings between status changes */}
            {detailedLog.timings?.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Transições de Status</p>
                {detailedLog.timings.map((t: any, i: number) => (
                  <p key={i} className="text-xs text-gray-600 dark:text-gray-400">
                    {t.from} → {t.to}: {t.durationHuman}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Adicionar Nota Interna</h2>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white resize-none"
              placeholder="Escreva uma nota sobre esta contestação..."
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setShowNoteModal(false); setAdminNote(''); }}
                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  await addNoteMutation.mutateAsync({ id: dispute.id, note: adminNote });
                  setShowNoteModal(false);
                  setAdminNote('');
                }}
                disabled={adminNote.trim().length < 3 || addNoteMutation.isPending}
                className="flex-1 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                {addNoteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote className="w-4 h-4" />}
                Salvar Nota
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reverse Modal */}
      {showReverseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-bold text-red-600 dark:text-red-400">⚠️ Estornar Crédito</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Esta ação irá debitar o valor bruto ({formatCurrency(grossAmount)}) da conta do cliente e devolver a taxa cobrada. O saldo do cliente será reduzido.
            </p>

            <div className="space-y-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Débito bruto:</span>
                <span className="font-medium text-red-600 dark:text-red-400">-{formatCurrency(grossAmount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Devolução taxa (5%):</span>
                <span className="font-medium text-green-600 dark:text-green-400">+{formatCurrency(fee)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-red-200 dark:border-red-800 pt-2">
                <span className="font-medium text-gray-900 dark:text-white">Impacto líquido:</span>
                <span className="font-bold text-red-700 dark:text-red-400">-{formatCurrency(netAmount)}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Justificativa (mín. 10 caracteres)
              </label>
              <textarea
                value={reverseNotes}
                onChange={(e) => setReverseNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white resize-none"
                placeholder="Motivo do estorno..."
              />
              {reverseNotes.length > 0 && reverseNotes.length < 10 && (
                <p className="text-xs text-red-500 mt-1">Mínimo 10 caracteres ({reverseNotes.length}/10)</p>
              )}
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={reverseConfirmed}
                onChange={(e) => setReverseConfirmed(e.target.checked)}
                className="w-4 h-4 rounded border-red-300 text-red-500 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Confirmo que desejo estornar este crédito
              </span>
            </label>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => { setShowReverseModal(false); setReverseNotes(''); setReverseConfirmed(false); }}
                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  await reverseMutation.mutateAsync({ id: dispute.id, notes: reverseNotes, confirmed: true });
                  setShowReverseModal(false);
                  setReverseNotes('');
                  setReverseConfirmed(false);
                }}
                disabled={reverseNotes.length < 10 || !reverseConfirmed || reverseMutation.isPending}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                {reverseMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                Estornar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Aprovar Contestação</h2>
            
            <div className="space-y-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Valor bruto:</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(grossAmount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Taxa (5%):</span>
                <span className="font-medium text-red-600 dark:text-red-400">-{formatCurrency(fee)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-green-200 dark:border-green-800 pt-2">
                <span className="font-medium text-gray-900 dark:text-white">Valor líquido:</span>
                <span className="font-bold text-green-700 dark:text-green-400">{formatCurrency(netAmount)}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Justificativa (mín. 10 caracteres)
              </label>
              <textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white resize-none"
                placeholder="Descreva o motivo da aprovação..."
              />
              {approveNotes.length > 0 && approveNotes.length < 10 && (
                <p className="text-xs text-red-500 mt-1">Mínimo 10 caracteres ({approveNotes.length}/10)</p>
              )}
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={approveConfirmed}
                onChange={(e) => setApproveConfirmed(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Confirmo que verifiquei os dados e autorizo o crédito
              </span>
            </label>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setShowApproveModal(false)}
                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleApprove}
                disabled={approveNotes.length < 10 || !approveConfirmed || approveMutation.isPending}
                className="flex-1 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                {approveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Aprovar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Rejeitar Contestação</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Motivo</label>
              <select
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value as RejectionReason)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white"
              >
                {REJECTION_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Detalhes (opcional)</label>
              <input
                type="text"
                value={rejectReasonDetail}
                onChange={(e) => setRejectReasonDetail(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white"
                placeholder="Detalhes adicionais..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Justificativa (mín. 10 caracteres)
              </label>
              <textarea
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white resize-none"
                placeholder="Descreva o motivo da rejeição..."
              />
              {rejectNotes.length > 0 && rejectNotes.length < 10 && (
                <p className="text-xs text-red-500 mt-1">Mínimo 10 caracteres ({rejectNotes.length}/10)</p>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={rejectNotes.length < 10 || rejectMutation.isPending}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                {rejectMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Rejeitar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DisputeReview;
